/**
 * TREATX-NOTIFICATION-ADMIN-OPS-11B
 * ----------------------------------
 * NotificationOpsDashboard
 *
 * Single TreatX Admin entry point for the operational view of the
 * canonical notification stack. NOT a new engine. NOT a second
 * pipeline. Purely an orchestrator that:
 *
 *  - shows lightweight metrics derived from the SAME `notifications_log`
 *    via `GET /api/admin/notifications/stats`,
 *  - links to the EXISTING canonical screens (templates editor at
 *    `/treatx-admin/email-templates`, commercial gating at
 *    `/treatx-admin/commercial-gating`),
 *  - links to the three new READ-ONLY sub-views that 11B introduces
 *    on top of canonical services:
 *      - Log Viewer
 *      - Consent Dashboard
 *      - Email Template Operations
 *
 * Architecture: every metric, every link, every action targets a
 * canonical endpoint that already existed before this sprint. The
 * only additive backend work was 4 READ-only endpoints under
 * `notification_admin_routes.py`.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Mail, MessageSquare, Shield, ShieldCheck,
  Activity, AlertTriangle, ArrowRight, Loader2, RefreshCw,
  ArrowLeft, FileText, Lock, ListChecks,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';

const StatCard = ({ icon: Icon, label, value, accent, testid, helper }) => (
  <div
    className={`bg-white border rounded-2xl p-4 flex flex-col gap-2 shadow-sm ${accent || ''}`}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
    </div>
    <div className="text-3xl font-semibold text-slate-900">{value}</div>
    {helper && <div className="text-[11px] text-slate-500">{helper}</div>}
  </div>
);

const SectionCard = ({ icon: Icon, title, description, onClick, badge, testid, accent }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left bg-white border rounded-2xl p-5 hover:border-blue-300 hover:shadow-md transition group w-full ${accent || ''}`}
    data-testid={testid}
  >
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <div className="flex items-start gap-2">
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          {badge && <Badge variant="outline" className="text-[10px]">{badge}</Badge>}
        </div>
        <p className="text-sm text-slate-500 mt-1">{description}</p>
      </div>
      <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-blue-500 transition" />
    </div>
  </button>
);

const NotificationOpsDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);

  const load = async () => {
    setReloading(true);
    try {
      const r = await api.get('/admin/notifications/stats', { params: { window_hours: 168 } });
      setStats(r.data || null);
    } catch (e) {
      setStats(null);
    } finally {
      setLoading(false);
      setReloading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const total = stats?.total ?? 0;
  const blockedGating = stats?.blocked_by_gating ?? 0;
  const blockedConsent = stats?.blocked_by_consent ?? 0;
  const byChannel = stats?.by_channel || [];
  const sent = (stats?.by_status || []).find(s => /sent/i.test(s.key || ''))?.count ?? 0;
  const failed = (stats?.by_status || []).find(s => /fail/i.test(s.key || ''))?.count ?? 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="notification-ops-dashboard">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/treatx-admin/dashboard')}
              className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
              data-testid="notification-ops-back-btn"
              aria-label="Back to admin dashboard"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Bell className="h-5 w-5 text-blue-600" />
                Notification Operations
              </h1>
              <p className="text-xs text-slate-500">Single operational view over the canonical notification stack — no parallel architecture.</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={reloading}
            data-testid="notification-ops-refresh-btn"
          >
            {reloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Metrics row */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-medium text-slate-700">Last 7 days</h2>
            {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              icon={Activity}
              label="Dispatched"
              value={total}
              testid="stat-total-dispatched"
              helper="all channels, all statuses"
            />
            <StatCard
              icon={ShieldCheck}
              label="Sent"
              value={sent}
              testid="stat-sent"
              helper="delivered to provider"
            />
            <StatCard
              icon={AlertTriangle}
              label="Blocked by gating"
              value={blockedGating}
              testid="stat-blocked-by-gating"
              accent={blockedGating > 0 ? 'border-amber-300' : ''}
              helper="WhatsApp / SMS commercial gate"
            />
            <StatCard
              icon={Lock}
              label="Blocked by consent"
              value={blockedConsent}
              testid="stat-blocked-by-consent"
              accent={blockedConsent > 0 ? 'border-rose-300' : ''}
              helper="per-event consent denied"
            />
          </div>
          {failed > 0 && (
            <div className="text-xs text-rose-600 mt-2" data-testid="stat-failed-banner">
              {failed} provider failure{failed === 1 ? '' : 's'} in the same window.
            </div>
          )}
        </div>

        {/* Channel breakdown */}
        {byChannel.length > 0 && (
          <div data-testid="stat-channel-breakdown">
            <h2 className="text-sm font-medium text-slate-700 mb-2">By channel</h2>
            <div className="flex flex-wrap gap-2">
              {byChannel.map(c => (
                <span
                  key={c.key}
                  className="px-3 py-1.5 rounded-full bg-white border text-xs text-slate-700 flex items-center gap-2"
                  data-testid={`stat-channel-${c.key}`}
                >
                  <span className="font-medium">{c.key}</span>
                  <span className="text-slate-400">·</span>
                  <span className="font-semibold text-slate-900">{c.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* 11B — new operational views */}
        <div>
          <h2 className="text-sm font-medium text-slate-700 mb-2">Operational views (read-only, on top of canonical services)</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <SectionCard
              icon={ListChecks}
              title="Notifications log"
              description="Chronological list of notifications_log with filters, search and drawer details."
              badge="11B"
              testid="nav-notifications-log"
              onClick={() => navigate('/treatx-admin/notification-ops/log')}
            />
            <SectionCard
              icon={Shield}
              title="Consent dashboard"
              description="Aggregated read-only view of ConsentService state. No new store."
              badge="11B"
              testid="nav-consent-dashboard"
              onClick={() => navigate('/treatx-admin/notification-ops/consent')}
            />
            <SectionCard
              icon={FileText}
              title="Email template operations"
              description="Per-template last-modified + last delivery status. Editor remains the canonical authority."
              badge="11B"
              testid="nav-template-status"
              onClick={() => navigate('/treatx-admin/notification-ops/templates-status')}
            />
            <SectionCard
              icon={MessageSquare}
              title="WhatsApp admin"
              description="Manage WhatsApp templates, set opt-outs, and test-send via the canonical endpoints."
              badge="11D"
              testid="nav-whatsapp-admin"
              onClick={() => navigate('/treatx-admin/notification-ops/whatsapp')}
            />
            <SectionCard
              icon={Shield}
              title="Content protection"
              description="Flagged threads, masking & block events, and risk telemetry from the canonical guard."
              badge="09C"
              testid="nav-content-protection"
              onClick={() => navigate('/treatx-admin/notification-ops/content-protection')}
            />
          </div>
        </div>

        {/* Existing canonical authorities (no duplication) */}
        <div>
          <h2 className="text-sm font-medium text-slate-700 mb-2">Existing canonical authorities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SectionCard
              icon={Mail}
              title="Email templates editor (canonical)"
              description="The single source of truth for email_templates. Edit subject + body, reset, test-send."
              badge="Existing"
              testid="nav-email-templates"
              onClick={() => navigate('/treatx-admin/email-templates')}
            />
            <SectionCard
              icon={MessageSquare}
              title="Commercial gating policy (canonical)"
              description="Channel gating for WhatsApp + SMS. Internal TreatX messaging is never gated."
              badge="Existing"
              testid="nav-commercial-gating"
              onClick={() => navigate('/treatx-admin/commercial-gating')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationOpsDashboard;
